import Seo from '@/components/Seo';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { motion } from 'framer-motion';
import { Users, Heart, MessageCircle, Award, Shield, Handshake } from 'lucide-react';
import whyUsImage from '@/assets/why-us-hurco.png';

const Team = () => {
  const values = [
    {
      icon: MessageCircle,
      title: 'Offenheit',
      description: 'Transparente Kommunikation auf allen Ebenen',
    },
    {
      icon: Heart,
      title: 'Ehrlichkeit',
      description: 'Aufrichtiger Umgang miteinander und mit Kunden',
    },
    {
      icon: Handshake,
      title: 'Respekt',
      description: 'Wertschätzung jedes einzelnen Teammitglieds',
    },
    {
      icon: Users,
      title: 'Teamgeist',
      description: 'Gemeinsam zum Erfolg – jeder zählt',
    },
    {
      icon: Award,
      title: 'Kompetenz',
      description: 'Fachliche Expertise in allen Bereichen',
    },
    {
      icon: Shield,
      title: 'Zuverlässigkeit',
      description: 'Verlässliche Partner für Ihre Projekte',
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Seo
        title="Team & Unternehmenskultur | SR-Zerspanung Stamsried"
        description="Vollblut-Zerspaner aus Stamsried: Lernen Sie das Team von SR-Zerspanung, Inhaber Thomas Schindler und unsere Werte in der CNC-Fertigung kennen."
        path="/team"
      />
      <Header />
      
      <main className="pt-32 md:pt-40 pb-20">
        <div className="container mx-auto px-4">
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-foreground mb-6">
              Unser Team
            </h1>
            <p className="text-muted-foreground text-lg max-w-3xl mx-auto">
              Menschen, die mit Leidenschaft und Präzision arbeiten
            </p>
          </motion.div>

          {/* Main Content */}
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto items-center mb-20">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={whyUsImage} 
                  alt="Thomas Schindler - Geschäftsführer SR-Zerspanung" 
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/50 via-transparent to-transparent" />
                {/* Name Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="text-white/80 text-sm uppercase tracking-wider">Geschäftsführer</p>
                  <p className="text-white font-heading font-bold text-2xl">Thomas Schindler</p>
                </div>
              </div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-fastlane/20 rounded-2xl -z-10" />
              <div className="absolute -top-4 -left-4 w-16 h-16 bg-smart/20 rounded-xl -z-10" />
            </motion.div>

            {/* Text */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground mb-6">
                Fachliche und persönliche Kompetenz
              </h2>
              <div className="bg-card rounded-2xl p-8 border border-border">
                <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                  Unser Team zeichnet sich durch fachliche und persönliche Kompetenz aus. Es wird jeder Zeit offen, ehrlich und respektvoll miteinander umgegangen, denn ein gutes Betriebsklima liegt uns sehr am Herzen.
                </p>
                <p className="text-foreground font-medium text-xl">
                  Ein gutes Umfeld ist ein fundamentaler Grundstein einer erfolgreichen Fertigung.
                </p>
              </div>
            </motion.div>
          </div>

          {/* Values Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="max-w-6xl mx-auto"
          >
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground text-center mb-12">
              Unsere Werte
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {values.map((value, index) => {
                const Icon = value.icon;
                return (
                  <motion.div
                    key={value.title}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                    className="group bg-card rounded-xl p-6 border border-border hover:border-primary/30 transition-all duration-300 hover:shadow-lg"
                  >
                    <div className="w-12 h-12 rounded-xl bg-primary/10 group-hover:bg-primary/20 flex items-center justify-center mb-4 transition-colors">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-heading font-bold text-lg text-foreground mb-2">
                      {value.title}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {value.description}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Team;